/**
 * Which Views another process may open, and how to read each one's context off
 * the wire. Split from the hook so it can be tested directly — nothing
 * type-checks between what `main/utils/security` emits and what this accepts.
 */

import { MEETING_VIEW_ID } from '../components/meeting/meeting-view-id'
import type { OpenViewFunction } from '@renderer/overlay/Sidepanel/view-atoms'

/** Wire payload for `sidepanel:openView`, sent by the main process and other windows. */
export interface OpenViewEventPayload {
  id: string
  context?: Record<string, unknown>
}

type WireContext = Record<string, unknown> | undefined

const readString = (context: WireContext, field: string): string | undefined => {
  const value = context?.[field]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * One validator per deeplinkable view. Deeplinks are untrusted input, so a view
 * is reachable this way only through an entry here, and registry membership
 * alone opens nothing.
 *
 * Each entry calls `openView` itself with a literal id, so the wire payload is
 * narrowed into a real context by the same code that consumes it — there is no
 * cast from `Record<string, unknown>` to a registry context anywhere. An entry
 * returning false rejects the open rather than pushing a View that would mount
 * without the id it exists to show.
 */
export const remoteOpeners: Record<string, (openView: OpenViewFunction, context: WireContext) => boolean> = {
  action: (openView, context) => {
    const actionId = readString(context, 'actionId')
    if (!actionId) {
      return false
    }
    openView('action', { actionId })
    return true
  },
  reminder: (openView, context) => {
    const reminderId = readString(context, 'reminderId')
    if (!reminderId) {
      return false
    }
    openView('reminder', { reminderId })
    return true
  },
  reminders: (openView) => {
    openView('reminders')
    return true
  },
  actions: (openView) => {
    openView('actions')
    return true
  },
  forYou: (openView) => {
    openView('forYou')
    return true
  },
  history: (openView) => {
    openView('history')
    return true
  },
  upcoming: (openView) => {
    openView('upcoming')
    return true
  },
  chat: (openView, context) => {
    const chatId = readString(context, 'chatId')
    if (!chatId) {
      return false
    }
    // Land on the newest messages, as the panel does when it opens a chat itself.
    openView('chat', { chatId, instantScrollToBottom: true })
    return true
  },
  chatOutput: (openView, context) => {
    const conversationId = readString(context, 'conversationId')
    if (!conversationId) {
      return false
    }
    openView('chatOutput', {
      conversationId,
      title: readString(context, 'title'),
      conversationType: readString(context, 'conversationType'),
      skillId: readString(context, 'skillId'),
      audioNoteId: readString(context, 'audioNoteId'),
    })
    return true
  },
  [MEETING_VIEW_ID]: (openView, context) => {
    const audioNoteId = readString(context, 'audioNoteId')
    const calendarEventId = readString(context, 'calendarEventId')
    if (!audioNoteId && !calendarEventId) {
      return false
    }
    openView(MEETING_VIEW_ID, {
      mode: 'note',
      audioNoteId,
      calendarEventId,
      title: readString(context, 'title'),
    })
    return true
  },
}
